"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, ArrowUpRight, Calendar, Clock, RefreshCw } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackInsights = [{
  id: "fallback-1",
  title: "How enterprise AI deployment is reshaping B2B buyer journeys",
  category: "AI & Technology",
  excerpt: "Enterprise procurement teams now use generative evaluations before vendor contact. We examine how sales and marketing alignment must adjust to stay visible during early evaluation phases.",
  date: "May 14, 2025",
  readTime: "6 min read",
  link: "/IndustryInsightsPage"
}, {
  id: "fallback-2",
  title: "The shift from lead volume to pipeline resilience in B2B revenue operations",
  category: "RevOps",
  excerpt: "Scaling pipeline without expanding customer acquisition cost requires disciplined data hygiene. Industry leaders are retiring vanity conversion metrics in favor of multi-touch account health scores.",
  date: "May 08, 2025",
  readTime: "8 min read",
  link: "/IndustryInsightsPage"
}, {
  id: "fallback-3",
  title: "Brand reputation as a risk buffer during volatile economic cycles",
  category: "Marketing",
  excerpt: "Longitudinal research shows decision-makers choose established domain authority over emerging low-cost competitors when market uncertainty increases by double digits.",
  date: "April 29, 2025",
  readTime: "5 min read",
  link: "/IndustryInsightsPage"
}];
function InsightsList() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="divide-y divide-border border-y border-border">
        {Array.from({
        length: 3
      }).map((_, i) => <div key={i} data-index={i} className="py-8 animate-pulse">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-5 w-24 bg-muted rounded-full" />
              <div className="h-4 w-28 bg-muted rounded-md" />
            </div>
            <div className="h-7 w-3/4 bg-muted rounded-md mb-3" />
            <div className="h-4 w-full bg-muted rounded-md mb-2" />
            <div className="h-4 w-2/3 bg-muted rounded-md" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="py-12 text-center border-y border-border">
        <p className="text-muted-foreground mb-4">Unable to load the latest industry analysis.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RefreshCw className="size-3.5" />
          Retry loading
        </Button>
      </div>;
  }
  const itemsToRender = posts && posts.length > 0 ? posts : fallbackInsights;
  return <div className="divide-y divide-border border-y border-border">
      {itemsToRender.map((item: any, i: number) => {
      const itemTitle = item.title || "Industry Analysis Briefing";
      const itemLink = item.link || "/IndustryInsightsPage";
      const itemExcerpt = item.excerpt || "Analytical examination of key market shifts, operational frameworks, and strategic benchmarks for enterprise decision-makers.";
      const itemDate = item.date || "Recent Analysis";
      const categoryName = item.categories && item.categories.length > 0 ? item.categories[0].name : item.category || "Research & Analysis";
      const readTime = item.readTime || "5 min read";
      return <article key={item.id || i} data-index={i} className="group py-8 sm:py-10 transition-colors duration-200 hover:bg-muted/30 px-2 sm:px-4">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div className="space-y-3 md:max-w-3xl">
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <Badge variant="secondary" className="rounded-full px-3 py-0.5 font-sans font-semibold tracking-wider uppercase text-[11px] bg-secondary text-secondary-foreground">
                    {categoryName}
                  </Badge>
                  <span className="inline-flex items-center gap-1">
                    <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                    {itemDate}
                  </span>
                  <span className="inline-flex items-center gap-1 font-mono text-[12px]">
                    <Clock className="size-3.5" />
                    {readTime}
                  </span>
                </div>

                <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground leading-snug group-hover:text-accent-foreground transition-colors duration-200">
                  <Link to={itemLink} className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    {itemTitle}
                  </Link>
                </h3>

                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed line-clamp-2">
                  {itemExcerpt}
                </p>
              </div>

              <div className="flex items-center md:self-center shrink-0 pt-2 md:pt-0">
                <Button asChild variant="ghost" size="sm" className="group/btn gap-2 text-foreground hover:text-foreground font-medium pl-0 md:pl-3">
                  <Link to={itemLink}>
                    <span>Read analysis</span>
                    <ArrowUpRight className="size-4 transition-transform duration-200 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Insights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latest-industry-insights" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Broadsheet Masthead Header */}
        <div className="mb-10 sm:mb-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 pb-6">
            <div className="space-y-2 max-w-2xl">
              <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
                Editorial Wire
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground">
                Latest industry insights
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Analytical commentary, market trends, and tactical frameworks prepared by our research desk.
              </p>
            </div>

            <Button asChild variant="outline" className="self-start sm:self-auto rounded-sm shrink-0">
              <Link to="/IndustryInsightsPage" className="inline-flex items-center gap-2">
                <span>View all articles</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          {/* Masthead Rule Motif: solid line + hairline separator */}
          <div className="space-y-1">
            <div className="h-[2px] w-full bg-primary" />
            <div className="h-[0.5px] w-full bg-border" />
          </div>
        </div>

        {/* Dynamic Post Feed */}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <InsightsList />
        </PostsProvider>
      </div>
    </section>;
}